/**
 * SPEYBOOKS | Migration Mode Layout
 * ============================================================================
 * @file      MigrationLayout.tsx
 * @version   1.0.0 (TMADD 7.2 Compliant)
 * @context   Dedicated "Clean Room" shell for data onboarding and migration.
 * Specifically designed to remove standard App Chrome to prevent
 * navigation-induced data corruption during sensitive operations.
 * * ─── FUNCTIONALITY ──────────────────────────────────────────────────────────
 * 1. LAYOUT ISOLATION:
 * - Removes Sidebar, Main Nav, and Footer.
 * - Provides a simplified 'Exit' path to Dashboard.
 * 2. STATE (isSealing):
 * - Controls the "Seal Sequence" (Full-screen takeover).
 * - When true, the Header is unmounted to prevent any escape during final
 * write operations to the ledger.
 * 3. SHARED CONTEXT:
 * - Exposes { isSealing, setIsSealing } via 'useMigrationLayout' hook
 * to all nested wizard steps.
 * * ─── TECHNICAL SPEC ─────────────────────────────────────────────────────────
 * - Ref: TMADD 7.2 §2 (Isolated Onboarding Environment)
 * - Constrained Width: max-w-3xl for focus-driven UI.
 * ============================================================================
 */
import { useState } from 'react';
import { Link, Outlet, useOutletContext } from 'react-router-dom';

interface MigrationLayoutContext {
  isSealing: boolean;
  setIsSealing: (v: boolean) => void;
}

export function useMigrationLayout(): MigrationLayoutContext {
  return useOutletContext<MigrationLayoutContext>();
}

export default function MigrationLayout() {
  const [isSealing, setIsSealing] = useState(false);

  // During seal sequence: full takeover, no header
  if (isSealing) {
    return <Outlet context={{ isSealing, setIsSealing }} />;
  }

  return (
    <div className="min-h-screen bg-surface-secondary flex flex-col">
      <header className="border-b border-edge bg-surface">
        <div className="flex items-center justify-between px-6 py-3 max-w-7xl mx-auto w-full">
          <img
            src="/logo.svg"
            alt="SpeyBooks"
            className="h-12 dark:hidden"
          />
          <img
            src="/logo-dark.svg"
            alt="SpeyBooks"
            className="h- 12 hidden dark:block"
          />
          <span className="text-sm font-semibold text-content-secondary">
            Migration
          </span>
          <Link
            to="/dashboard"
            className="text-sm text-content-tertiary hover:text-content transition-colors"
          >
            Exit to Dashboard
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-5xl px-6 py-8">
          <Outlet context={{ isSealing, setIsSealing }} />
        </div>
      </main>
    </div>
  );
}
